// src/validation/flatValidation.js
import * as Yup from "yup";
import { LATE_FEE_TYPES, LATE_FEE_LIMITS } from "../../../utils/latePolicy";

// Shared shape for Maintenance / Water / Security / Trash charges: an amount
// plus how often it's billed. When "annual", the owner must pick a due month
// so the smart bill-upload flow knows which cycle to surface it in.
export const chargeSchema = Yup.object().shape({
  amount: Yup.number()
    .typeError("Must be a number")
    .required("Amount is required")
    .min(0, "Cannot be negative"),
  frequency: Yup.string()
    .oneOf(["monthly", "annual"], "Must be monthly or annual")
    .required(),
  dueMonth: Yup.number()
    .min(1, "Must be between 1-12")
    .max(12, "Must be between 1-12")
    .when("frequency", {
      is: "annual",
      then: (schema) => schema.required("Please select the due month"),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

// Empty inputs hold "" — treat them as "not filled in" rather than a NaN type
// error, so a disabled policy (or a field that doesn't apply to the chosen fee
// type) never blocks the form.
const optionalNumber = (message) =>
  Yup.number()
    .transform((value, original) =>
      original === "" || original === null || original === undefined ? undefined : value
    )
    .typeError(message);

// Applies `build` only while the policy is enabled and (optionally) one of the
// given fee types is selected; otherwise the field is not validated at all.
const whenApplicable = (types, build) => ([enabled, type], schema) =>
  enabled === true && (!types || types.includes(type)) ? build(schema) : schema;

const rentOf = (ctx) => Number(ctx.from?.[1]?.value?.flatRent);
const ceilingFor = (rent) => (rent * LATE_FEE_LIMITS.MAX_CAP_PERCENTAGE) / 100;
const CEILING_MESSAGE = `Cannot exceed ${LATE_FEE_LIMITS.MAX_CAP_PERCENTAGE}% of the monthly rent`;

// The property's DEFAULT late-payment policy. Nothing is calculated here; the
// fee is only worked out later, per bill. Ceilings that are a share of the
// monthly rent read `flatRent` from the parent form.
export const latePolicySchema = Yup.object().shape({
  enabled: Yup.boolean(),
  type: Yup.string().when(
    ["enabled"],
    whenApplicable(null, (s) =>
      s.oneOf(Object.values(LATE_FEE_TYPES), "Select a fee type").required("Fee type is required")
    )
  ),
  amount: optionalNumber("Late fee must be a number").when(
    ["enabled", "type"],
    whenApplicable(null, (s) =>
      s
        .required("Late fee amount is required")
        .moreThan(0, "Must be greater than 0")
        .test(
          "daily-percentage-limit",
          `Cannot exceed ${LATE_FEE_LIMITS.MAX_DAILY_PERCENTAGE}% of rent per day`,
          function (value) {
            if (this.parent.type !== LATE_FEE_TYPES.PER_DAY_PERCENTAGE || value === undefined) return true;
            return value <= LATE_FEE_LIMITS.MAX_DAILY_PERCENTAGE;
          }
        )
        .test("fixed-within-rent-ceiling", CEILING_MESSAGE, function (value) {
          if (this.parent.type !== LATE_FEE_TYPES.FIXED_AFTER_GRACE) return true;
          const rent = rentOf(this);
          return !rent || value === undefined || value <= ceilingFor(rent);
        })
    )
  ),
  gracePeriodDays: optionalNumber("Grace period must be a number").when(
    ["enabled", "type"],
    whenApplicable(null, (s) =>
      s
        .required("Grace period is required")
        .integer("Must be a whole number of days")
        .min(0, "Cannot be negative")
        .max(LATE_FEE_LIMITS.MAX_GRACE_DAYS, `Cannot exceed ${LATE_FEE_LIMITS.MAX_GRACE_DAYS} days`)
    )
  ),
  maximumAmount: optionalNumber("Maximum late fee must be a number").when(
    ["enabled", "type"],
    whenApplicable([LATE_FEE_TYPES.PER_DAY_FIXED], (s) =>
      s
        .required("Maximum late fee is required")
        .moreThan(0, "Must be greater than 0")
        .test("cap-not-below-daily", "Cannot be less than the daily late fee", function (value) {
          const daily = Number(this.parent.amount);
          return !daily || value === undefined || value >= daily;
        })
        .test("cap-within-rent-ceiling", CEILING_MESSAGE, function (value) {
          const rent = rentOf(this);
          return !rent || value === undefined || value <= ceilingFor(rent);
        })
    )
  ),
  maximumPercentage: optionalNumber("Maximum late fee must be a number").when(
    ["enabled", "type"],
    whenApplicable([LATE_FEE_TYPES.PER_DAY_PERCENTAGE], (s) =>
      s
        .required("Maximum late fee is required")
        .moreThan(0, "Must be greater than 0")
        .max(LATE_FEE_LIMITS.MAX_CAP_PERCENTAGE, CEILING_MESSAGE)
        .test("cap-not-below-daily", "Cannot be less than the daily late fee", function (value) {
          const daily = Number(this.parent.amount);
          return !daily || value === undefined || value >= daily;
        })
    )
  ),
});

export const flatValidationSchema = Yup.object().shape({
  flatName: Yup.string().required("Property name is required"),
  flatNumber: Yup.string().required("Property number is required"),
  flatFloor: Yup.number()
    .typeError("Floor must be a number")
    .required("Floor is required")
    .min(0, "Floor cannot be negative"),
  flatRooms: Yup.number()
    .typeError("Rooms must be a number")
    .required("Number of rooms is required")
    .min(0, "Cannot be negative"),
  flatToilet: Yup.number()
    .typeError("Toilets must be a number")
    .required("Toilets are required")
    .min(0, "Cannot be negative"),
  flatKitchen: Yup.number()
    .typeError("Kitchen must be a number")
    .required("Kitchen count is required")
    .min(0, "Cannot be negative"),
  flatRent: Yup.number()
    .typeError("Rent must be a number")
    .required("Rent is required")
    .min(1000, "Minimum rent must be at least 1000"),
  flatAdvance: Yup.number()
    .typeError("Advance must be a number")
    .required("Advance is required")
    .min(0, "Cannot be negative"),
  maintenance: chargeSchema,
  water: chargeSchema,
  security: chargeSchema,
  trash: chargeSchema,
  personAllowed: Yup.number()
    .typeError("Allowed Person must be a number")
    .required("Person is required")
    .min(0, "Cannot be negative"),
  previews: Yup.array()
    .min(1, "At least one image is required")
    .required("At least one image is required"),
  // Late-payment policy is the admin's platform-wide setting now (see
  // SystemSettings.latePaymentPolicy) — the owner's form only displays it,
  // disabled, and the server stamps the current admin value on save
  // regardless of what's submitted here. latePolicySchema (still exported,
  // still covered by its own tests) validated the owner's OWN input under the
  // old per-property model; wiring it into this form again would validate a
  // value the owner never chose against ceilings (e.g. % of THIS property's
  // rent) that have nothing to do with a single global admin setting.
  latePolicy: Yup.mixed().notRequired(),
  propertyAddress: Yup.string().required("Property Address is required"),
  note: Yup.string(),
  is_rent: Yup.string(),
  // tags: Yup.array()
  //   .of(Yup.string())
  //   .min(1, "Property Highlights are required. Please add at least one highlight."),
  allowedFor: Yup.string()
    .oneOf(["family", "bachelor","both"], "Please select either Family Bachelor or Both")
    .required("Suitable For is required"),
  mapAddress: Yup.string(),
  lat: Yup.number(),
  lng: Yup.number(),
  landlordConditions: Yup.array().of(Yup.string()),
  customConditions: Yup.string(),
  rentDueDate: Yup.number()
    .min(1, "Must be between 1-28")
    .max(28, "Must be between 1-28"),
  noticePeriod: Yup.string(),
  rentIncrease: Yup.number()
    .min(0, "Cannot be negative")
    .max(50, "Maximum 50%"),
  leaseDuration: Yup.string(),
});

// Predefined Landlord Conditions
export const landlordConditionOptions = [
  {
    value: "no_pets",
    label: "No Pets Allowed",
    description: "Tenant cannot keep any pets on the property",
  },
 
  {
    value: "no_smoking",
    label: "No Smoking Inside",
    description: "Smoking is not allowed inside the property",
  },
  {
    value: "no_parties",
    label: "No Loud Parties",
    description: "No loud parties or gatherings after 10 PM",
  },
  {
    value: "maintain_cleanliness",
    label: "Maintain Cleanliness",
    description: "Property must be kept clean and hygienic",
  },
  {
    value: "no_structural_changes",
    label: "No Structural Changes",
    description:
      "No drilling, painting or structural modifications without permission",
  },
  {
    value: "water_conservation",
    label: "Water Conservation",
    description: "Tenant must conserve water and pay for any wastage",
  },
  {
    value: "electricity_bills",
    label: "Utility Bills by Tenant",
    description:
      "All utility bills (electricity, gas, water) to be paid by tenant",
  },
  {
    value: "prior_notice_visit",
    label: "24hr Notice for Visits",
    description: "Landlord will give 24 hours notice before visiting",
  },
  {
    value: "guest_policy",
    label: "Guest Policy",
    description:
      "Overnight guests limited to 3 consecutive days without prior approval",
  },
  {
    value: "parking_rules",
    label: "Parking Rules",
    description: "Only designated parking space to be used",
  },
  {
    value: "security_deposit_terms",
    label: "Security Deposit Terms",
    description: "Security deposit refundable within 30 days after vacating",
  },
];

// Lease Duration Options
export const leaseDurationOptions = [
  { value: "6_months", label: "6 Months" },
  { value: "11_months", label: "11 Months" },
  { value: "1_year", label: "1 Year" },
  { value: "2_years", label: "2 Years" },
  { value: "flexible", label: "Flexible" },
];

// Notice Period Options
export const noticePeriodOptions = [
  { value: "15_days", label: "15 Days" },
  { value: "1_month", label: "1 Month" },
  { value: "2_months", label: "2 Months" },
  { value: "3_months", label: "3 Months" },
];

export const bill_uploaded_by_Options = [
  { value: 'landlord', label: 'Owner' },
  { value: 'tenant', label: 'Tenant' },

];